import _ from "lodash";
import api from "@/api";
import * as projectTypes from "@/helpers/projectTypes";
import * as projectAddons from "@/helpers/projectAddons";

export default {
  namespaced: true,
  state: {
    project: {},
    is_within_time_delay: false,
    project_created_at: null,
    max_wet_stamp_per_project: 8,
    is_structural_plan_stamped: false,
    iebc_project_types: [3, 4, 21, 22, 23, 24, 25, 26],
    states: [],
    recently_uploaded_files: [],
    possible_cities_contractor: null,
    full_project_addr_contractor: "",
    original_designer: null
  },
  getters: {
    upgradable: (state, getters) => {
      return !getters.is_project_type_max;
    },
    is_project_type_max: state => {
      /**
       * Note: Project Type is MAX if
       * project type lengths === 3 AND
       * there is Sure-Cert in the mix
       */

      const pd = projectTypes.plansetDesign();

      if (state.project.project_types.includes(pd.value)) {
        return true;
      }

      const FS = projectTypes.fullStructural();
      const has_fs = state.project.project_types.includes(FS.value);

      return state.project.project_types.length === 3 && has_fs;
    },
    is_project_iebc: state => {
      const project_types = _.get(state, "project.project_types", []);
      const projectType = projectTypes.iebcLetter();
      const projectTypeAdd = projectTypes.iebcLetterWithStampedPlans();

      if (projectType || projectTypeAdd) {
        return (
          !!project_types.find(id => id === projectType.value) ||
          !!project_types.find(id => id === projectTypeAdd.value)
        );
      }

      return false;
    },
    is_project_iebc_only: state => {
      const project_types = _.get(state, "project.project_types", []);
      const projectType = projectTypes.iebcLetter();

      if (projectType) {
        return (
          project_types.length == 1 &&
          !!project_types.find(id => id === projectType.value)
        );
      }

      return false;
    },
    is_project_planset_design_only: state => {
      const project_types = _.get(state, "project.project_types", []);
      const projectType = projectTypes.plansetDesign();

      if (projectType) {
        return (
          project_types.length == 1 &&
          !!project_types.find(id => id === projectType.value)
        );
      }

      return false;
    },
    is_available_wet_stamp_count: state => {
      const request_list = _.get(
        state,
        "project.project_wetstamp_requests",
        []
      );
      let count = 0;

      if (request_list.length > 0) {
        count = request_list.reduce((accumulator, currentValue) => {
          return accumulator + currentValue.wet_stamps;
        }, 0);
      }

      return state.max_wet_stamp_per_project - count;
    },
    is_post_install_letter_required: state => {
      if (state.project) {
        return state.project.project_documents.some(
          item => item.project_document_type.key === "POST_INSTALL_PHOTOS"
        );
      }

      return false;
    },
    is_project_full_calcs_express: state => {
      const full_calcs = _.get(state, "project.project_meta.full_calcs", []);

      return !!full_calcs;
    },
    is_project_express: state => {
      const is_express = _.get(state, "project.is_express", []);

      return is_express;
    },

    is_electrical_stamp: state => {
      const project_types = _.get(state, "project.project_types", []);
      const projectType = projectTypes.electricalStamp();

      if (projectType) {
        return (
          project_types.length >= 1 &&
          !!_.find(project_types, item => item === projectType.value)
        );
      }

      return false;
    },

    is_electrical_fault_study: state => {
      const project_types = _.get(state, "project.project_types", []);
      const projectType = projectTypes.electricalFaultStudy();

      if (projectType) {
        return (
          project_types.length >= 1 &&
          !!_.find(project_types, item => item === projectType.value)
        );
      }

      return false;
    },

    is_only_electrical: state => {
      const project_types = _.get(state, "project.project_types", []);
      const esType = projectTypes.electricalStamp();
      const efsType = projectTypes.electricalFaultStudy();

      return (
        project_types.length <= 2 &&
        _.every(
          project_types,
          item => item === esType.value || item === efsType.value
        )
      );
    },

    is_full_structural: state => {
      const project_types = _.get(state, "project.project_types", []);
      const projectType = projectTypes.fullStructural();

      if (projectType) {
        return (
          project_types.length >= 1 &&
          !!_.find(project_types, item => item === projectType.value)
        );
      }

      return false;
    },

    is_consumption_report_review: state => {
      const project_addons = _.get(state, "project.project_addons", []);
      const projectAddon = projectAddons.pzseConsumptionReportReview();

      if (projectAddon) {
        return !!project_addons.find(value => value.type === projectAddon.id);
      }

      return false;
    },

    customPricing: state => {
      return _.get(state, "project.custom_pricings", []);
    },

    is_residential_yard: state => {
      const name = state.project.project_meta.mount_type?.name;
      if (name) return name.startsWith("Ground Mount");
      return (
        state.project.project_meta.building_description?.name ===
        "Residential Yard/Open Field"
      );
    },

    is_ground_mount: state => {
      const name = state.project.project_meta.mount_type?.name;
      return name.startsWith("Ground Mount");
    },

    is_ballasted: state => {
      const name = state.project.project_meta.mount_type?.name;

      return name === "Ballasted";
    }
  },
  mutations: {
    SET_PROJECT_CREATED_AT: (state, payload) =>
      (state.project_created_at = payload),
    SET_PROJECT: (state, payload) => {
      // check for discrepancy,
      // note: remove block if addressed in BE
      // by adjusting `project_types` accordingly (for iebc_sp projects)
      const iebc_sp_id = 4;
      const is_there_iebc_sp = payload.project_types.some(
        type => type === iebc_sp_id
      );
      if (payload.project_type.value === iebc_sp_id && !is_there_iebc_sp) {
        payload.project_types.splice(0, 1, iebc_sp_id);
      }
      // remove block if addressed in BE
      state.project = payload;
    },
    SET_ENGINEERING_VALUES: (state, payload) =>
      (state.project.engineering_values = payload),
    SET_PLANSET_DESIGN_VALUES: (state, payload) =>
      (state.project.project_meta.designer = payload),
    SET_PLANSET_DESIGN_CONTRACTOR_VALUES: (state, payload) =>
      (state.project.project_meta.designer.contractor = payload),
    SET_TRACKING: (state, payload) => {
      state.project.shipping_profile.tracking_number = payload.tracking_number;
      state.project.shipping_profile.tracking_url = payload.tracking_url;
    },
    SET_IS_WITHIN_TIME_DELAY: (state, payload) =>
      (state.is_within_time_delay = payload),
    SET_IS_STRUCTURAL_PLAN_STAMPED: (state, payload) =>
      (state.is_structural_plan_stamped = payload),
    SET_STATES: (state, payload) => (state.states = payload),
    SET_RECENTLY_UPLOADED_FILES: (state, payload) =>
      (state.recently_uploaded_files = payload),

    SET_POSSIBLE_CITIES_CONTRACTOR: (state, payload) =>
      (state.possible_cities_contractor = payload),

    SET_FULL_ADDR_CONTRACTOR: (state, payload) =>
      (state.full_project_addr_contractor = payload),

    SET_ORIGINAL_DESIGNER: (state, payload) =>
      (state.original_designer = payload)
  },
  actions: {
    getStates: async ({ commit }) => {
      const result = await api.get("/providers/states");
      const data = _.get(result, "data.result", []);
      commit("SET_STATES", data);

      return data;
    },
    getData: async ({ commit }, { id }) => {
      // eslint-disable-next-line no-useless-catch
      try {
        const response = await api.get(`/projects/${id}`);
        const project = _.get(response, "data.result");

        commit("SET_PROJECT", project);

        return response;
      } catch (error) {
        throw error;
      }
    },
    setFormdataLocationContractorAddress: async (
      { commit, state },
      payload
    ) => {
      // CHECKING OF CITY before changing address
      const possible_cities = state.possible_cities_contractor;
      const possible_city_value = [
        "locality",
        "sublocality",
        "neighborhood",
        "administrative_area_level_3"
      ];

      let available_city = "";

      // whatever is available in the dropdown is the correct city
      possible_city_value.some(prop => {
        let possible_city = _.get(possible_cities, prop, null);

        if (possible_city) available_city = possible_city;

        return possible_city;
      });

      payload.address.city = available_city;

      commit("SET_PLANSET_DESIGN_CONTRACTOR_VALUES", payload);
    }
  }
};
